import type { BlobClient } from '@jbroll/rowboat-client';
import type { Graph } from '@/schema/estate';
import { MAX_IDENTIFY_IMAGES } from '../../shared/identify';
import { classifyDownload, fetchPhotoBlob, isRefusedSign, PhotoFetchError } from './fetchPhoto';
import { blobToBase64, type PostOutcome, ProtocolError, postIdentify } from './identifyClient';
import { cachedIdentifyImage, shrinkForIdentify } from './identifyImages';
import type { JobOutcome, RunJob } from './identifyQueue';
import { photoQueueStore } from './photoQueue';
import { photosForItem, readMedia } from './photoService';
import { recordSuggestions } from './suggestionService';

// A photo this old that isn't in this device's outbox was lost before its upload committed;
// identify from the photos that did arrive.
const UPLOAD_WAIT_MS = 10 * 60_000;

// Under the backend's 32 MB body limit, whose 413 would fail the job on every retry.
const MAX_BASE64_TOTAL = 30 * 1024 * 1024;

class ShrinkFailed extends Error {}

interface Encoded {
  photoId: string;
  base64: string;
  isThumb: boolean;
  thumbHash: string | undefined;
}

function downloadFailure(err: unknown): JobOutcome {
  if (!(err instanceof PhotoFetchError)) return 'transient';
  // Refused until the push carrying the photo's key lands; MAX_TRANSIENT ends one that never does.
  if (isRefusedSign(err)) return 'transient';
  const outcome = classifyDownload(err.status);
  if (outcome === 'permanent') console.warn(`identify: photo download refused ${err.status}`);
  return outcome === 'permanent' || outcome === 'transient' ? outcome : { unavailable: outcome };
}

export function createIdentifyRunner(
  g: Graph,
  deps: {
    client?: BlobClient;
    fetchBytes?: (hash: string) => Promise<Blob>;
    shrink?: (bytes: Blob) => Promise<Blob>;
    cached?: (photoId: string) => Promise<Blob | undefined>;
    post?: (images: string[]) => Promise<PostOutcome>;
    pendingUploads?: () => Promise<string[]>;
    now?: () => number;
    maxBase64Total?: number;
  } = {},
): RunJob {
  const pendingUploads = deps.pendingUploads ?? (() => photoQueueStore().list());
  const fetchBytes =
    deps.fetchBytes ?? ((hash: string) => fetchPhotoBlob(hash, { client: deps.client }));
  const shrink = deps.shrink ?? shrinkForIdentify;
  const cached = deps.cached ?? cachedIdentifyImage;
  const post = deps.post ?? ((images: string[]) => postIdentify(images));
  const now = deps.now ?? Date.now;
  const maxBase64Total = deps.maxBase64Total ?? MAX_BASE64_TOTAL;

  const asThumb = async (photoId: string, thumbHash: string): Promise<Encoded> => ({
    photoId,
    base64: await blobToBase64(await fetchBytes(thumbHash)),
    isThumb: true,
    thumbHash,
  });

  async function encodePhoto(
    photoId: string,
    shrunkAtCapture: Blob | undefined,
    originalHash: string,
    thumbHash: string | undefined,
  ): Promise<Encoded> {
    if (shrunkAtCapture) {
      return { photoId, base64: await blobToBase64(shrunkAtCapture), isThumb: false, thumbHash };
    }
    let bytes: Blob;
    try {
      bytes = await fetchBytes(originalHash);
    } catch (err) {
      if (thumbHash === undefined) throw err;
      return asThumb(photoId, thumbHash);
    }
    try {
      return {
        photoId,
        base64: await blobToBase64(await shrink(bytes)),
        isThumb: false,
        thumbHash,
      };
    } catch (err) {
      if (thumbHash === undefined) throw new ShrinkFailed(String(err));
      console.warn('identify: original shrink failed, sending thumbnail', photoId, err);
      return asThumb(photoId, thumbHash);
    }
  }

  async function fitBudget(encoded: Encoded[]): Promise<Encoded[]> {
    const total = encoded.reduce((sum, e) => sum + e.base64.length, 0);
    if (total <= maxBase64Total) return encoded;
    const fitted: Encoded[] = [];
    for (const e of encoded) {
      if (e.isThumb) fitted.push(e);
      else if (e.thumbHash !== undefined) fitted.push(await asThumb(e.photoId, e.thumbHash));
    }
    return fitted;
  }

  return async (itemId, { thumbnails, stillBound }): Promise<JobOutcome> => {
    const row = g.item(itemId)?.$data;
    // Items are only soft-deleted, so a missing row means this graph hasn't loaded it yet.
    if (!row) return 'wait';
    if (row.archived) return 'drop';
    const t = now();
    const hashOf = (cell: unknown) => readMedia(cell)?.hash;
    const sources = [];
    for (const photo of photosForItem(g, itemId)) {
      const original = hashOf(photo.storage_key);
      const thumb = hashOf(photo.thumbnail_key);
      // Equal hashes mean the thumbnail derivative failed at upload: there is no separate thumbnail.
      sources.push({
        photo,
        image: await cached(photo.id),
        original,
        thumb: thumb === original ? undefined : thumb,
      });
    }
    // A thumbnail exists only once its photo has uploaded, so a cached image can't stand in for it.
    const missing = sources.filter((s) => s.original === undefined && (thumbnails || !s.image));
    if (missing.length > 0) {
      // Offline, a key uploaded elsewhere can't sync here, so a missing photo isn't known lost.
      if (navigator.onLine === false) return { unavailable: 'offline' };
      // An outbox photo uploads however long the backlog takes; its commit nudges this job.
      const queued = new Set(await pendingUploads());
      if (missing.some((s) => queued.has(s.photo.id) || t - s.photo.created_at < UPLOAD_WAIT_MS))
        return 'wait';
    }
    const readable = sources
      .filter((s) => (thumbnails ? s.thumb !== undefined : s.image || s.original !== undefined))
      .slice(0, MAX_IDENTIFY_IMAGES);
    if (readable.length === 0) return sources.length > 0 ? 'permanent' : 'drop';

    let encoded: Encoded[];
    try {
      const prepared: Encoded[] = [];
      // One at a time: each decoded original is tens of MB.
      for (const { photo, image, original, thumb } of readable) {
        prepared.push(
          thumbnails && thumb !== undefined
            ? await asThumb(photo.id, thumb)
            : await encodePhoto(photo.id, image, original ?? '', thumb),
        );
      }
      encoded = await fitBudget(prepared);
    } catch (err) {
      return err instanceof ShrinkFailed ? 'permanent' : downloadFailure(err);
    }
    if (encoded.length === 0) return 'permanent';
    // The queue discards this outcome and leaves the job as it was.
    if (!stillBound()) return 'wait';

    let outcome: PostOutcome;
    try {
      outcome = await post(encoded.map((e) => e.base64));
    } catch (err) {
      if (!(err instanceof ProtocolError)) throw err;
      console.warn('identify: protocol error, failing the job', err);
      return 'permanent';
    }
    if (outcome.kind === 'daily_limit')
      return { unavailable: 'daily_limit', retryAfterMs: outcome.retryAfterMs };
    if (outcome.kind === 'unavailable') return { unavailable: outcome.reason };
    if (outcome.kind !== 'ok') return outcome.kind;
    await recordSuggestions(
      g,
      itemId,
      outcome.result,
      encoded.map((e) => e.photoId),
    );
    return 'done';
  };
}
